import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Zap, CalendarClock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/lib/store";
import { inr } from "@/components/badges";
import { startRazorpay, TestPaymentModal } from "@/components/RazorpayCheckout";
import { SaveListButton } from "@/components/SaveListButton";

export default function CartPage() {
  const { user, cart, setQty, removeItem, clearCart, zoneId, setZoneId } = useStore();
  const [meta, setMeta] = useState({ zones: [] });
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [payment, setPayment] = useState("cod");
  const [subs, setSubs] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [testPay, setTestPay] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.address && !address) setAddress(user.address); if (user?.phone && !phone) setPhone(user.phone); }, [user]); // eslint-disable-line

  useEffect(() => {
    setQuote(null);
    setQuoteError("");
    if (!cart.length || !user) return;
    api.post("/orders/quote", {
      items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty })),
      zone_id: zoneId,
    }).then((r) => setQuote(r.data))
      .catch((e) => setQuoteError(formatApiError(e)));
  }, [cart, zoneId, user]);

  const finish = (data, paid) => {
    clearCart();
    toast.success(`Order ${data.order_no} ${paid ? "paid & " : ""}placed — ${data.mode === "express" ? "Express 90-min SLA" : "Scheduled freight"}`);
    navigate(`/orders/${data.id}`);
  };

  const placeOrder = async () => {
    if (!address.trim()) { toast.error("Enter a delivery address"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty })),
        zone_id: zoneId, address, phone, payment_method: payment, substitutions_allowed: subs,
      });
      if (payment !== "razorpay") { finish(data, false); return; }
      try {
        const stub = await startRazorpay(data.id, (paidOrder) => finish(paidOrder, true));
        if (stub) setTestPay({ rp: stub, orderId: data.id });
      } catch (e) {
        toast.error(e?.message || formatApiError(e));
        clearCart();
        navigate(`/orders/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlacing(false);
    }
  };

  if (!user) return <p className="py-16 text-center text-sm text-slate-500" data-testid="cart-login-hint">Please <Link to="/auth" className="text-orange-700 font-semibold">sign in</Link> to check out.</p>;
  if (!cart.length) return (
    <div className="py-24 text-center" data-testid="cart-empty">
      <p className="font-display text-2xl font-bold text-slate-900">Your cart is empty</p>
      <Link to="/buyer" className="btn-brand mt-6 inline-flex" data-testid="cart-browse-btn">Browse materials</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="cart-page">
      <p className="overline-label">Checkout</p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Review order</h1>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500 mt-1">Single-vendor fulfilment · {cart[0].vendor_name}</p>
        <SaveListButton items={cart} source="cart" testid="cart-save-list" />
      </div>
      {testPay && <TestPaymentModal rp={testPay.rp} orderId={testPay.orderId} onPaid={(o) => { setTestPay(null); finish(o, true); }} onClose={() => { setTestPay(null); clearCart(); navigate(`/orders/${testPay.orderId}`); }} />}

      <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        <div className="space-y-4">
          {cart.map((c) => (
            <div key={c.product_id} data-testid={`cart-item-${c.sku}`} className="card-surface p-4 flex gap-4 items-center">
              <img src={c.image} alt={c.name} className="h-16 w-16 rounded-lg object-cover bg-slate-100" />
              <div className="flex-1 min-w-0">
                <p className="font-mono2 text-[11px] text-slate-400">{c.sku}</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                <p className="text-xs text-slate-500">{inr(c.price)} / {c.unit} · MOQ {c.moq}</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" min={c.moq} value={c.qty} data-testid={`cart-qty-${c.sku}`}
                  onChange={(e) => setQty(c.product_id, parseInt(e.target.value || c.moq, 10))}
                  className="input-b2b !w-20 text-center" />
                <button data-testid={`cart-remove-${c.sku}`} onClick={() => removeItem(c.product_id)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}

          <div className="card-surface p-5 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery zone</label>
              <select data-testid="checkout-zone-select" value={zoneId} onChange={(e) => setZoneId(e.target.value)} className="input-b2b mt-2">
                <option value="">Select zone</option>
                {meta.zones.map((z) => <option key={z.id} value={z.id}>{z.name}{z.express_supported ? "" : " (scheduled only)"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery address</label>
              <textarea data-testid="checkout-address-input" rows={2} value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="Site / office address with landmark" className="input-b2b mt-2" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">WhatsApp number for order alerts</label>
              <input data-testid="checkout-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxx" className="input-b2b mt-2" />
              <p className="mt-1 text-[11px] text-slate-500">Email alerts go to {user.email}. WhatsApp updates on accept, dispatch and delivery.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[["cod", "Cash on delivery"], ["razorpay", "Pay online (UPI/Card)"], ["credit", "Credit terms"]].map(([v, l]) => (
                  <button key={v} data-testid={`payment-${v}`} onClick={() => setPayment(v)}
                    className={`rounded-lg border px-3 py-2.5 text-xs font-semibold ${payment === v ? "border-orange-700 bg-orange-50 text-orange-800" : "border-slate-300 text-slate-600"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {payment === "credit" && <p className="mt-2 text-[11px] text-slate-500">Credit terms subject to admin approval of your business profile.</p>}
              {payment === "razorpay" && <p className="mt-2 text-[11px] text-slate-500">Secure Razorpay checkout — UPI, cards, netbanking. Order is confirmed to the vendor once payment is captured.</p>}
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="substitutions-toggle" checked={subs} onChange={(e) => setSubs(e.target.checked)} className="mt-1 h-4 w-4 accent-orange-700" />
              <span>Allow approved substitutes if an exact SKU becomes unavailable after ordering (otherwise that line is refunded automatically).</span>
            </label>
          </div>
        </div>

        <aside className="card-surface p-6 sticky top-24" data-testid="checkout-summary">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery estimate</p>
          {quoteError && <p className="mt-3 text-sm text-red-600" data-testid="quote-error">{quoteError}</p>}
          {quote && (
            <>
              <div className={`mt-3 rounded-xl border p-4 ${quote.mode === "express" ? "border-amber-500/40 bg-amber-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`} data-testid="eta-panel">
                {quote.mode === "express" ? (
                  <p className="flex items-center gap-2 text-sm font-bold text-amber-800"><Zap className="h-4 w-4" /> Express · ETA {quote.eta_minutes} min</p>
                ) : (
                  <p className="flex items-center gap-2 text-sm font-bold text-emerald-800"><CalendarClock className="h-4 w-4" /> Scheduled · {quote.scheduled_slot}</p>
                )}
                <p className="mt-1 text-[11px] text-slate-600">Exact ETA confirmed before payment. No blanket promises.</p>
              </div>
              {!quote.all_available && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="unavailable-warning">
                  Some lines are out of stock. Remove them to proceed.
                  {quote.lines.filter((l) => !l.available).map((l) => (
                    <div key={l.product.id} className="mt-2">
                      <p className="font-semibold">{l.product.name}</p>
                      {l.substitutes?.length > 0 && <p className="mt-1">Substitutes: {l.substitutes.map((s) => s.name).join(", ")}</p>}
                    </div>
                  ))}
                </div>
              )}
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-semibold">{inr(quote.subtotal)}</dd></div>
                {quote.discount > 0 && (
                  <div className="flex justify-between text-emerald-700" data-testid="checkout-discount"><dt>{quote.promo?.label}</dt><dd className="font-semibold">− {inr(quote.discount)}</dd></div>
                )}
                <div className="flex justify-between"><dt className="text-slate-500">GST (18%)</dt><dd className="font-semibold">{inr(quote.gst)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Delivery ({quote.mode})</dt><dd className="font-semibold">{inr(quote.delivery_fee)}</dd></div>
                <div className="flex justify-between border-t border-slate-200 pt-2"><dt className="font-bold text-slate-900">Total</dt><dd className="font-display font-bold text-lg" data-testid="checkout-total">{inr(quote.total)}</dd></div>
              </dl>
              <button onClick={placeOrder} disabled={placing || !quote.all_available || !zoneId} data-testid="place-order-btn"
                className="btn-brand w-full mt-5 !py-3">
                {placing ? "Placing order…" : payment === "razorpay" ? `Pay ${inr(quote.total)} & place order` : "Place order"}
              </button>
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Vendor must accept within 10 min or the order auto-escalates.</p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
